<script setup lang="ts">
interface Circle {
  alpha: number
  dx: number
  dy: number
  magnetism: number
  size: number
  targetAlpha: number
  translateX: number
  translateY: number
  x: number
  y: number
}

interface Props {
  blurStrength?: number
  color?: string
  ease?: number
  maxSize?: number
  minSize?: number
  quantity?: number
  staticity?: number
}

const props = withDefaults(defineProps<Props>(), {
  blurStrength: 0,
  color: '#FFF',
  ease: 50,
  maxSize: 3,
  minSize: 1,
  quantity: 100,
  staticity: 50,
})

const canvasRef = ref<HTMLCanvasElement>()
const canvasContainerRef = ref<HTMLDivElement>()
const context = ref<CanvasRenderingContext2D>()
const circles = ref<Circle[]>([])
const mouse = reactive<{ x: number, y: number }>({
  x: 0,
  y: 0,
})
const canvasSize = reactive<{ height: number, width: number }>({
  height: 0,
  width: 0,
})
const { x: mouseX, y: mouseY } = useMouse()
const { pixelRatio } = useDevicePixelRatio()
const isVisible = useElementVisibility(canvasContainerRef)
const animationFrameId = ref<number>()
const offscreenCanvas = ref<HTMLCanvasElement>()
const offscreenContext = ref<CanvasRenderingContext2D>()

const filterId = `svg-particles-blur-${Math.random().toString(36).substring(2, 9)}`

const color = computed(() => {
  // Remove the leading '#' if it's present
  let hex = props.color.replace(/^#/, '')

  // If the hex code is 3 characters, expand it to 6 characters
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(char => char + char)
      .join('')
  }

  // Parse the r, g, b values from the hex string
  const bigint = Number.parseInt(hex, 16)
  const red = (bigint >> 16) & 255 // Extract the red component
  const green = (bigint >> 8) & 255 // Extract the green component
  const blue = bigint & 255 // Extract the blue component

  // Return the RGB values as a string separated by spaces
  return `${red} ${green} ${blue}`
})

onMounted(() => {
  if (canvasRef.value) {
    context.value = canvasRef.value.getContext('2d', { alpha: true }) as CanvasRenderingContext2D

    // Create offscreen canvas for better performance
    offscreenCanvas.value = document.createElement('canvas')
    offscreenContext.value = offscreenCanvas.value.getContext('2d', { alpha: true }) as CanvasRenderingContext2D
  }

  initCanvas()
  startAnimation()
  window.addEventListener('resize', debouncedResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', debouncedResize)
  stopAnimation()
})

// Debounced resize handler
const debouncedResize = useDebounceFn(initCanvas, 150)

// Throttled mouse move handler
const throttledMouseMove = useThrottleFn(
  () => {
    onMouseMove()
  },
  10,
)

watch([mouseX, mouseY], throttledMouseMove)

// Watch visibility to pause animation when not visible
watch(isVisible, (visible) => {
  if (visible) {
    startAnimation()
  }
  else {
    stopAnimation()
  }
})

function initCanvas() {
  resizeCanvas()
  drawParticles()
}

function startAnimation() {
  if (!animationFrameId.value) {
    animate()
  }
}

function stopAnimation() {
  if (animationFrameId.value) {
    window.cancelAnimationFrame(animationFrameId.value)
    animationFrameId.value = undefined
  }
}

function onMouseMove() {
  if (canvasRef.value) {
    const rect = canvasRef.value.getBoundingClientRect()
    const { height, width } = canvasSize
    const x = mouseX.value - rect.left - width / 2
    const y = mouseY.value - rect.top - height / 2

    const inside = x < width / 2 && x > -width / 2 && y < height / 2 && y > -height / 2

    if (inside) {
      mouse.x = x
      mouse.y = y
    }
  }
}

function resizeCanvas() {
  if (canvasContainerRef.value && canvasRef.value && context.value) {
    circles.value.length = 0
    canvasSize.width = canvasContainerRef.value.offsetWidth
    canvasSize.height = canvasContainerRef.value.offsetHeight

    // Set up main canvas
    canvasRef.value.width = canvasSize.width * pixelRatio.value
    canvasRef.value.height = canvasSize.height * pixelRatio.value
    canvasRef.value.style.width = `${canvasSize.width}px`
    canvasRef.value.style.height = `${canvasSize.height}px`
    context.value.scale(pixelRatio.value, pixelRatio.value)

    // Set up offscreen canvas with the same dimensions
    if (offscreenCanvas.value && offscreenContext.value) {
      offscreenCanvas.value.width = canvasRef.value.width
      offscreenCanvas.value.height = canvasRef.value.height
      offscreenContext.value.scale(pixelRatio.value, pixelRatio.value)
    }
  }
}

function circleParams(): Circle {
  const x = Math.floor(Math.random() * canvasSize.width)
  const y = Math.floor(Math.random() * canvasSize.height)
  const translateX = 0
  const translateY = 0
  const size = Math.floor(Math.random() * (props.maxSize - props.minSize + 1)) + props.minSize
  const alpha = 0
  const targetAlpha = Number.parseFloat((Math.random() * 0.6 + 0.1).toFixed(1))
  const dx = (Math.random() - 0.5) * 0.2
  const dy = (Math.random() - 0.5) * 0.2
  const magnetism = 0.1 + Math.random() * 4

  return {
    alpha,
    dx,
    dy,
    magnetism,
    size,
    targetAlpha,
    translateX,
    translateY,
    x,
    y,
  }
}

// Draw all circles in a single batch operation
function drawAllCircles(ctx: CanvasRenderingContext2D) {
  ctx.clearRect(0, 0, canvasSize.width, canvasSize.height)

  // Group circles by alpha for batching
  const circlesByAlpha = new Map<string, Circle[]>()

  circles.value.forEach((circle) => {
    const alphaKey = circle.alpha.toFixed(2)

    if (!circlesByAlpha.has(alphaKey)) {
      circlesByAlpha.set(alphaKey, [])
    }
    circlesByAlpha.get(alphaKey)!.push(circle)
  })

  // Draw circles in batches by alpha value
  circlesByAlpha.forEach((circleGroup, alphaKey) => {
    ctx.fillStyle = `rgba(${color.value.split(' ').join(', ')}, ${alphaKey})`
    ctx.beginPath()

    circleGroup.forEach((circle) => {
      ctx.moveTo(circle.x + circle.translateX + circle.size, circle.y + circle.translateY)
      ctx.arc(circle.x + circle.translateX, circle.y + circle.translateY, circle.size, 0, Math.PI * 2)
    })

    ctx.fill()
  })
}

function drawCircle(circle: Circle, update = false) {
  if (context.value) {
    const { alpha, size, translateX, translateY, x, y } = circle

    context.value.beginPath()
    context.value.arc(x + translateX, y + translateY, size, 0, 2 * Math.PI)
    context.value.fillStyle = `rgba(${color.value.split(' ').join(', ')}, ${alpha})`
    context.value.fill()

    if (!update) {
      circles.value.push(circle)
    }
  }
}

function clearContext() {
  if (context.value) {
    context.value.clearRect(0, 0, canvasSize.width, canvasSize.height)
  }
}

function drawParticles() {
  clearContext()
  const particleCount = props.quantity

  for (let index = 0; index < particleCount; index++) {
    const circle = circleParams()

    drawCircle(circle)
  }
}

function remapValue(
  value: number,
  start1: number,
  end1: number,
  start2: number,
  end2: number,
): number {
  const remapped = ((value - start1) * (end2 - start2)) / (end1 - start1) + start2

  return remapped > 0 ? remapped : 0
}

function animate() {
  if (!isVisible.value) {
    animationFrameId.value = window.requestAnimationFrame(animate)

    return
  }

  if (offscreenContext.value) {
    offscreenContext.value.clearRect(0, 0, canvasSize.width, canvasSize.height)
  }

  // Create arrays to store circles to keep and remove
  const circlesOutOfBounds: number[] = []

  // Update circles
  circles.value.forEach((circle, index) => {
    // Handle the alpha value
    const edge = [
      circle.x + circle.translateX - circle.size, // distance from left edge
      canvasSize.width - circle.x - circle.translateX - circle.size, // distance from right edge
      circle.y + circle.translateY - circle.size, // distance from top edge
      canvasSize.height - circle.y - circle.translateY - circle.size, // distance from bottom edge
    ]

    const closestEdge = edge.reduce((aEdge, bEdge) => Math.min(aEdge, bEdge))
    const remapClosestEdge = Number.parseFloat(remapValue(closestEdge, 0, 20, 0, 1).toFixed(2))

    if (remapClosestEdge > 1) {
      circle.alpha += 0.02
      if (circle.alpha > circle.targetAlpha) {
        circle.alpha = circle.targetAlpha
      }
    }
    else {
      circle.alpha = circle.targetAlpha * remapClosestEdge
    }

    circle.x += circle.dx
    circle.y += circle.dy
    circle.translateX
      += (mouse.x / (props.staticity / circle.magnetism) - circle.translateX) / props.ease
    circle.translateY
      += (mouse.y / (props.staticity / circle.magnetism) - circle.translateY) / props.ease

    // check if circle is out of canvas
    if (
      circle.x < -circle.size
      || circle.x > canvasSize.width + circle.size
      || circle.y < -circle.size
      || circle.y > canvasSize.height + circle.size
    ) {
      // Mark for removal instead of removing immediately
      circlesOutOfBounds.push(index)
    }
  })

  // Sort indices in descending order to avoid shifting problems when splicing
  circlesOutOfBounds.sort((aIndex, bIndex) => bIndex - aIndex)

  // Remove circles out of bounds and create new ones in a batch
  circlesOutOfBounds.forEach((index) => {
    circles.value.splice(index, 1)
    circles.value.push(circleParams())
  })

  // Use offscreen canvas for rendering if available
  if (offscreenContext.value && context.value) {
    drawAllCircles(offscreenContext.value)
    context.value.clearRect(0, 0, canvasSize.width, canvasSize.height)
    context.value.drawImage(offscreenCanvas.value as CanvasImageSource, 0, 0)
  }
  else if (context.value) {
    // Fallback to direct rendering
    drawAllCircles(context.value)
  }

  animationFrameId.value = window.requestAnimationFrame(animate)
}
</script>

<template>
  <div
    ref="canvasContainerRef"
    aria-hidden="true"
    :class="{
      'blur-lg': blurStrength > 0 && $device.isSafari,
    }"
  >
    <svg
      v-if="blurStrength > 0 && !$device.isSafari"
      class="absolute"
      height="0"
      width="0"
    >
      <defs>
        <filter :id="filterId">
          <feGaussianBlur :stdDeviation="blurStrength" />
        </filter>
      </defs>
    </svg>

    <canvas
      ref="canvasRef"
      :style="blurStrength > 0 ? { filter: `url(#${filterId})` } : undefined"
    />
  </div>
</template>
